import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

export interface PostalLookupResult {
  found: boolean;
  state: string;
  district: string;
  taluk: string;
  location: string;
}

const asRecord = (value: unknown): Record<string, unknown> =>
  typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : {};

const asString = (value: unknown): string => (typeof value === 'string' ? value : '');

const NOT_FOUND: PostalLookupResult = {
  found: false,
  state: '',
  district: '',
  taluk: '',
  location: ''
};

/** Total by contract: a pincode with no post office is `found: false`, never a throw. */
function normalizePostal(response: unknown): PostalLookupResult {
  const first = asRecord(Array.isArray(response) ? response[0] : response);
  if (first.Status !== 'Success' || !Array.isArray(first.PostOffice) || !first.PostOffice.length) {
    return NOT_FOUND;
  }
  const po = asRecord(first.PostOffice[0]);
  return {
    found: true,
    state: asString(po.State),
    district: asString(po.District),
    taluk: asString(po.Division),
    location: asString(po.Name)
  };
}

/**
 * Its own `createApi` because it is a third-party host with a different base url and no
 * auth: sending the app's `Authorization` header to india post would leak the session token.
 */
export const postalServiceApi = createApi({
  reducerPath: 'postalServiceApi',
  baseQuery: fetchBaseQuery({ baseUrl: 'https://api.postalpincode.in/' }),
  endpoints: (builder) => ({
    lookupPincode: builder.query<PostalLookupResult, { pincode: string }>({
      query: ({ pincode }) => ({ url: `pincode/${pincode}` }),
      transformResponse: (response: unknown) => normalizePostal(response),
      keepUnusedDataFor: 3600
    })
  })
});

export const { useLookupPincodeQuery } = postalServiceApi;
